'use client';


import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { EditMasterclassFormType } from '@/components/forms/masterclass/EditMasterclassForm';
import { openPdfLinksModal } from '@/components/masterclass/pdf-links-modal';
import { useToast } from '@/components/ui/use-toast';
import { IMasterclass, PdfDownloadLink } from '@/entities/masterclass/types';
import { queryClient } from '@/shared/api/query-client';
import { QUERY_KEYS } from '@/shared/const/query-keys';
import { useSession } from '@/shared/hooks/use-session';
import { useApi } from '@/shared/hooks/use-api';
import { getRandomInt } from '@/shared/utils/getRandomInt';
import { getApiUrl } from '@/shared/utils/getApiUrl';
import { apiClient } from '@/shared/api/api-client';
import { revalidateMasterclassesFromAdmin } from '@/shared/api/revalidate-client';

export const useGetAllMasterclassesQuery = (options?: { enabled?: boolean }) => {
  const { isPending, token } = useSession();
  const enabled = options?.enabled ?? true;

  return useQuery({
    queryKey: [QUERY_KEYS.masterclasses],
    queryFn: async () => {
      return await apiClient.get(
        getApiUrl(`/masterclasses`),
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined,
      );
    },
    select: (data): IMasterclass[] => {
      return data.data.results;
    },
    enabled: !isPending && enabled,
    staleTime: 10 * 60 * 1000,
  });
};

// export const useGetTop5MasterclassesQuery = () => {
//   const { isPending, token } = useSession();
//   return useQuery({
//     queryKey: [QUERY_KEYS.top5],
//     queryFn: async () => {
//       return await axios.get(
//         getApiUrl(`/top-5?populate=*`),
//         token
//           ? {
//               headers: {
//                 Authorization: `Bearer ${token}`,
//               },
//             }
//           : undefined,
//       );
//     },
//     select: (res): IMasterclass[] =>
//       res.data.attributes?.masterclasses?.data ?? [],
//     enabled: !isPending,
//   });
// };

export type UseGetMasterclassByIdOptions = {
  /** SSR/SSG снимок — сразу в UI; затем refetch с токеном для inCart / isBuyed / isLiked */
  initialMasterclass?: IMasterclass;
};

export const useGetmasterclassById = (
  id: string,
  options?: UseGetMasterclassByIdOptions,
) => {
  const { isPending, token } = useSession();
  const { toast } = useToast();

  return useQuery({
    queryKey: ['masterclass', id, token ?? 'anon'],
    queryFn: async (): Promise<IMasterclass> => {
      try {
        const response = await apiClient.get(
          getApiUrl(`/masterclasses/${id}?populate=categories`),
          token
            ? {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            : undefined,
        );

        const body = response.data as IMasterclass & { error?: unknown };
        if (body && typeof body === 'object' && 'error' in body && body.error) {
          console.error('Error in response:', body.error);
          throw new Error('Ошибка при получении данных мастер-класса');
        }

        return body as IMasterclass;
      } catch (error) {
        console.error('Error fetching masterclass:', error);
        if (options?.initialMasterclass) {
          return options.initialMasterclass;
        }
        toast({
          title: 'Ошибка при загрузке мастер-класса',
          variant: 'error',
          description:
            error instanceof Error ? error.message : 'Неизвестная ошибка',
        });
        throw error;
      }
    },
    placeholderData: options?.initialMasterclass,
    enabled: !isPending && !!id,
    retry: 1,
  });
};

export const useUpdateMasterclassMutation = (id?: number) => {
  const api = useApi();
  const router = useRouter();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: EditMasterclassFormType) => {
      return await api.put(`/masterclasses/${id}`, JSON.stringify({ data }), {
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },
    onSuccess: async (res) => {
      toast({
        title: 'Мастер-класс обновлен',
        variant: 'success',
      });
      try {
        await revalidateMasterclassesFromAdmin();
      } catch (e) {
        console.warn('[useUpdateMasterclassMutation] revalidate', e);
      }
      router.push(`/masterclasses/${res.data.wordId}`);
    },
    onError: () => {
      toast({
        title: 'Произошла ошибка при обновлении мастер-класса',
        variant: 'error',
      });
    },
  });
};

export const useCreateMasterclassMutation = () => {
  const api = useApi();
  const router = useRouter();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: EditMasterclassFormType) => {
      return await api.post(
        `/masterclasses`,
        JSON.stringify({ data: { ...data, id: getRandomInt() } }),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
    },
    onSuccess: async (response, _data) => {
      toast({
        title: 'Мастер-класс создан',
        variant: 'success',
      });
      if (response.data.id) {
        try {
          await revalidateMasterclassesFromAdmin();
        } catch (e) {
          console.warn('[useCreateMasterclassMutation] revalidate', e);
        }
        router.push(`/masterclasses/${response.data.wordId}`);
      } else {
        toast({
          title: 'В ответе отсутствует id',
          variant: 'error',
        });
      }
    },
    onError: (error) => {
      console.error(error);
      toast({
        title: 'Произошла ошибка при создании мастер-класса',
        variant: 'error',
        description: error.message,
      });
    },
  });
};

export const useDownloadMasterclassMutation = () => {
  const { token } = useSession();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ mcId }: { mcId: number }) => {
      // format=links — бэк отдаёт подписанные ссылки на S3 вместо base64,
      // файл качает сам браузер (с прогрессом и без лимитов на размер ответа).
      return await apiClient.get(getApiUrl(`/pdf/download/${mcId}`), {
        params: { format: 'links' },
        headers: token
          ? {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            }
          : undefined,
      });
    },
    onSuccess: (response) => {
      const files: PdfDownloadLink[] = response.data?.files ?? [];

      // Ссылка с Content-Disposition: attachment — страница остаётся на месте, файл скачивается.
      if (files.length === 1) {
        window.location.assign(files[0].url);
        return;
      }
      openPdfLinksModal(files);
    },
    onError: (error) => {
      console.error(error);
      toast({
        title: 'Произошла ошибка при скачивании мастер-класса',
        variant: 'error',
        // description: error.message,
      });
    },
  });
};

export const useSetDiscountFromCategoryMutation = () => {
  const api = useApi();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      categoryId,
      discount,
    }: {
      categoryId: number;
      discount: number;
    }) => {
      return await api.put(
        `/masterclass/set-discount-for-category`,
        JSON.stringify({ data: { discount, categoryId } }),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
    },
    onSuccess: async (response) => {
      if (response?.data?.error) {
        return toast({
          title: 'Произошла ошибка при установке скидки',
          variant: 'error',
        });
      }
      toast({
        title: 'Скидка обновлена',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['masterclasses'] });
      try {
        await revalidateMasterclassesFromAdmin();
      } catch (e) {
        console.warn('[useSetDiscountFromCategoryMutation] revalidate', e);
      }
    },
    onError: () => {
      toast({
        title: 'Произошла ошибка при обновлении мастер-классов',
        variant: 'error',
      });
    },
  });
};

export const useSendMasterclassManualMutation = () => {
  const api = useApi();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { email: string; mcIds: string[] }) => {
      return await api.post(
        `/masterclass/send-mc-manual`,
        JSON.stringify({ data: { ...data } }),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
    },
    onSuccess: (_response, _data) => {
      toast({
        title: 'МК отправлен',
        variant: 'success',
      });
    },
    onError: (error) => {
      console.error(error);
      toast({
        title: 'Произошла ошибка при отправке мастер-класса',
        variant: 'error',
        description: error.message,
      });
    },
  });
};

export const useGetTop5MasterclassesQuery = () => {
  const { isPending, token } = useSession();
  return useQuery({
    queryKey: [QUERY_KEYS.top5],
    queryFn: async () => {
      return await apiClient.get(
        getApiUrl(`/top-5`),
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined,
      );
    },
    select: (data): IMasterclass[] => {
      return data.data;
    },
    enabled: !isPending,
  });
};

/**
 * Хук для обновления порядка мастер-классов
 */
export const useUpdateMasterclassesOrderMutation = () => {
  const { token } = useSession();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (
      masterclasses: Pick<IMasterclass, 'id' | 'order'>[],
    ) => {
      return await apiClient.put(
        getApiUrl(`/masterclasses/update-order`),
        {
          masterclasses,
        },
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined,
      );
    },
    onSuccess: () => {
      toast({
        title: 'Порядок мастер-классов обновлен',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.masterclasses] });
    },
    onError: () => {
      toast({
        title: 'Произошла ошибка при обновлении порядка мастер-классов',
        variant: 'error',
      });
    },
  });
};
